import { SimpleTable } from '@app/components/base/SimpleTable';
import { LDateTime } from '@app/components/date/LDateTime';
import { PntStationStatusItemDtoV2 } from '@mi-front/core';
import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { MentionConfidence } from '../../../../components/mentions/MentionConfidence';
import {
  scheduleStatusDetailActionsMessage,
  scheduleStatusDetailMatchDatetimeMessage,
  scheduleStatusDetailMatchedScheduleMessage,
  scheduleStatusDetailNoMatchedScheduleMessage,
  scheduleStatusDetailUnmatchedMatchesMessage,
} from '../spot/schedule-status-spot-messages';
import { scheduleStatusMentionsConfidenceMessage } from './schedule-status-mentions-messages';
import { getUnmatchedMentionsMatches } from './schedule-status-mentions-utils';
import { ScheduleStatusMentionsDetailMatchActions } from './ScheduleStatusMentionsDetailMatchActions';

interface ScheduleStatusMentionsDetailUnmatchedMatchesProps {
  item: PntStationStatusItemDtoV2;
}

/**
 * Detections that did not confirm against any slot schedule. Hidden when every match is linked.
 */
export function ScheduleStatusMentionsDetailUnmatchedMatches({
  item,
}: ScheduleStatusMentionsDetailUnmatchedMatchesProps) {
  const unmatched = [...getUnmatchedMentionsMatches(item)].sort((a, b) =>
    a.datetime.localeCompare(b.datetime),
  );

  if (unmatched.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        <FormattedMessage {...scheduleStatusDetailUnmatchedMatchesMessage} />
      </Typography>
      <SimpleTable
        fixedLayout
        columns={[
          {
            header: {
              content: <FormattedMessage {...scheduleStatusDetailMatchDatetimeMessage} />,
            },
            headerProps: { sx: { width: '35%', whiteSpace: 'nowrap' } },
            renderCell: (match) => ({
              content: <LDateTime value={match.datetime} showRelative />,
            }),
          },
          {
            header: {
              content: <FormattedMessage {...scheduleStatusMentionsConfidenceMessage} />,
            },
            headerProps: { align: 'center', sx: { width: '20%' } },
            renderCell: (match) => ({
              content: <MentionConfidence value={match.confidence} />,
              props: { align: 'center' },
            }),
          },
          {
            header: {
              content: <FormattedMessage {...scheduleStatusDetailMatchedScheduleMessage} />,
            },
            headerProps: { align: 'center', sx: { width: '30%' } },
            renderCell: () => ({
              content: (
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage {...scheduleStatusDetailNoMatchedScheduleMessage} />
                </Typography>
              ),
              props: { align: 'center' },
            }),
          },
          {
            header: {
              content: <FormattedMessage {...scheduleStatusDetailActionsMessage} />,
            },
            headerProps: { align: 'center', sx: { width: '15%', whiteSpace: 'nowrap' } },
            renderCell: (match) => ({
              content: <ScheduleStatusMentionsDetailMatchActions match={match} />,
              props: { align: 'center' },
            }),
          },
        ]}
        data={unmatched}
      />
    </Box>
  );
}
